import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestDetails = ({ request, onClose, onApprove, onReject, onAssignReviewer, currentUser }) => {
  const [showDiff, setShowDiff] = useState(true);
  const [comment, setComment] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);

  if (!request) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="FileSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Select a Request</h3>
        <p className="text-sm text-muted-foreground">
          Choose a change request from the table to view details
        </p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning/10 text-warning border-warning/20';
      case 'approved': return 'bg-success/10 text-success border-success/20';
      case 'rejected': return 'bg-error/10 text-error border-error/20';
      case 'in-review': return 'bg-accent/10 text-accent border-accent/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canApprove = currentUser?.role === 'admin' || currentUser?.role === 'reviewer';
  const isAssigned = request?.reviewer === currentUser?.name;

  const handleAddComment = () => {
    if (comment?.trim()) {
      // In a real app, this would make an API call
      console.log('Adding comment:', comment);
      setComment('');
      setShowCommentForm(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-foreground">Request Details</h3>
            <span className="font-mono text-sm text-primary font-medium">
              {request?.requestId}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            iconSize={18}
            onClick={onClose}
          />
        </div>
      </div>
      <div className="p-4 space-y-6">
        {/* Request Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Document</label>
              <div className="flex items-center gap-2 mt-1">
                <Icon name="FileText" size={16} className="text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">{request?.documentName}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Requester</label>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center">
                  <Icon name="User" size={12} color="white" />
                </div>
                <span className="text-sm text-foreground">{request?.requester}</span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Submission Date</label>
              <p className="text-sm text-foreground mt-1">{formatDate(request?.submissionDate)}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
                  {request?.status?.replace('-', ' ')}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Priority</label>
              <div className="flex items-center gap-1 mt-1">
                <Icon 
                  name="AlertCircle" 
                  size={14} 
                  className={getPriorityColor(request?.priority)}
                />
                <span className={`text-sm capitalize ${getPriorityColor(request?.priority)}`}>
                  {request?.priority}
                </span>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Reviewer</label>
              <p className="text-sm text-foreground mt-1">
                {request?.reviewer || 'Unassigned'}
              </p>
            </div>
          </div>
        </div>

        {/* Justification */}
        <div>
          <label className="text-sm font-medium text-muted-foreground">Justification</label>
          <div className="mt-2 p-3 bg-muted/30 rounded-lg">
            <p className="text-sm text-foreground">{request?.justification}</p>
          </div>
        </div>

        {/* Content Comparison */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-muted-foreground">Content Changes</label>
            <div className="flex items-center gap-2">
              <Button
                variant={showDiff ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDiff(true)}
              >
                Diff View
              </Button>
              <Button
                variant={!showDiff ? "default" : "outline"}
                size="sm"
                onClick={() => setShowDiff(false)}
              >
                Side by Side
              </Button>
            </div>
          </div>

          {showDiff ? (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/30 p-2 text-xs font-medium text-muted-foreground border-b border-border">
                Unified Diff View
              </div>
              <div className="p-4 font-mono text-sm space-y-1 bg-background max-h-64 overflow-y-auto">
                <div className="text-error">- {request?.originalContent}</div>
                <div className="text-success">+ {request?.proposedContent}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-error/10 p-2 text-xs font-medium text-error border-b border-border">
                  Original Content
                </div>
                <div className="p-4 text-sm bg-background max-h-64 overflow-y-auto">
                  {request?.originalContent}
                </div>
              </div>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-success/10 p-2 text-xs font-medium text-success border-b border-border">
                  Proposed Content
                </div>
                <div className="p-4 text-sm bg-background max-h-64 overflow-y-auto">
                  {request?.proposedContent}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Attachments */}
        {request?.attachments && request?.attachments?.length > 0 && (
          <div>
            <label className="text-sm font-medium text-muted-foreground">Attachments</label>
            <div className="mt-2 space-y-2">
              {request?.attachments?.map((attachment, index) => (
                <div key={index} className="flex items-center gap-3 p-2 border border-border rounded-lg">
                  <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground flex-1">{attachment?.name}</span>
                  <span className="text-xs text-muted-foreground">{attachment?.size}</span>
                  <Button variant="ghost" size="sm" iconName="Download" iconSize={14}>
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-muted-foreground">Comments & History</label>
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              iconSize={14}
              onClick={() => setShowCommentForm(!showCommentForm)}
            >
              Add Comment
            </Button>
          </div>

          {showCommentForm && (
            <div className="mb-4 p-3 border border-border rounded-lg bg-muted/30">
              <textarea
                value={comment}
                onChange={(e) => setComment(e?.target?.value)}
                placeholder="Add your comment..."
                className="w-full p-2 border border-border rounded text-sm bg-background text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                rows={3}
              />
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={handleAddComment}>
                  Add Comment
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowCommentForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {request?.history?.map((item, index) => (
              <div key={index} className="flex gap-3 p-3 border border-border rounded-lg">
                <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={14} color="white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-foreground">{item?.user}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(item?.timestamp)}</span>
                  </div>
                  <p className="text-sm text-foreground">{item?.action}</p>
                  {item?.comment && (
                    <p className="text-sm text-muted-foreground mt-1 italic">{item?.comment}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        {canApprove && request?.status === 'pending' && (
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <Button
              variant="success"
              iconName="Check"
              iconSize={16}
              onClick={() => onApprove(request?.id)}
            >
              Approve
            </Button>
            <Button
              variant="destructive"
              iconName="X"
              iconSize={16}
              onClick={() => onReject(request?.id)}
            >
              Reject
            </Button>
            {!isAssigned && (
              <Button
                variant="outline"
                iconName="UserPlus"
                iconSize={16}
                onClick={() => onAssignReviewer(request?.id, currentUser?.name)}
              >
                Assign to Me
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestDetails;